import React, { useState, useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { KTSVG } from '../../../../rbts/helpers';

type Props = {
  data: any[];
  splitPuntoVenta: number;
  onGetDataProducts: (products: any) => void;
};

const ProductsList: React.FC<Props> = ({ data, splitPuntoVenta, onGetDataProducts }) => {
  const [dataMemo, setDataMemo] = useState<any[]>(data);
  const isFirstRender = useRef(true);

  const productsSelected = (updatedDataMemo: any) => {
    onGetDataProducts(updatedDataMemo);
  };

  const AddRow = () => {
    const cleanedData = dataMemo.filter((item) => item.id !== 0);
    const newRow: any = {
      id: Math.floor(1000000000 + Math.random() * 9000000000),
      nombre: '',
      precio_venta: 0,
      util_proveedor: 0,
      aplica_costo_fijo_cortesia: false,
      costo_fijo_cortesia: 0,
      isNew: true,
    };
    setDataMemo([...cleanedData, newRow]);
  };

  const deleteRowById = (productId: number) => {
    setDataMemo((prevDataMemo) => {
      const updatedDataMemo = prevDataMemo.filter(
        (item) => item.id !== productId
      );
      productsSelected(updatedDataMemo);
      return updatedDataMemo;
    });
  };

  useEffect(() => {
    if (data.length) {
      const cleanedData = data.filter((item) => item?.id !== 0);
      setDataMemo(cleanedData);
    }
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const updated = dataMemo.map((item) => ({
      ...item,
      util_proveedor: parseFloat(((item.precio_venta || 0) * (splitPuntoVenta / 100)).toFixed(2)),
    }));
    setDataMemo(updated);
    productsSelected(updated);
  }, [splitPuntoVenta]);

  const costChange = (precio_venta: number, id: any) => {
    const costo = precio_venta || 0;
    const updatedItems = dataMemo.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          precio_venta: costo,
          util_proveedor: parseFloat((costo * (splitPuntoVenta / 100)).toFixed(2)),
        };
      }
      return item;
    });
    setDataMemo(updatedItems);
    productsSelected(updatedItems);
  };

  const handleNameProduct = (nombreProducto: any, id: any) => {
    if (nombreProducto) {
      const updatedItems = dataMemo.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            nombre: nombreProducto ? nombreProducto : '',
          };
        }
        return item;
      });
      setDataMemo(updatedItems);
      productsSelected(updatedItems);
    }
  };

  const handleCortesiaCheck = (checked: boolean, id: any) => {
    const updatedItems = dataMemo.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          aplica_costo_fijo_cortesia: checked,
        };
      }
      return item;
    });
    setDataMemo(updatedItems);
    productsSelected(updatedItems);
  };

  const handleUtilProveedor = (util_proveedor: number, id: any) => {
    const updatedItems = dataMemo.map((item) => {
      if (item.id === id) {
        return { ...item, util_proveedor: util_proveedor || 0 };
      }
      return item;
    });
    setDataMemo(updatedItems);
    productsSelected(updatedItems);
  };

  const handleCostoCortesia = (costo_fijo_cortesia: number, id: any) => {
    const updatedItems = dataMemo.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          costo_fijo_cortesia: costo_fijo_cortesia ? costo_fijo_cortesia : 0,
        };
      }
      return item;
    });
    setDataMemo(updatedItems);
    productsSelected(updatedItems);
  };

  return (
    <>
      <div className="table-responsive min-h-300px me-n5 pe-5 scroll-y w-100 mt-5">
        <table className="table table-border table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 me-n5 pe-5 w-100">
          <thead>
            <tr className="text-gray-800 fs-7">
              <th className="w-30">Nombre</th>
              <th className="w-15">Costo</th>
              <th className="w-15">Util. proveedor</th>
              <th className="w-10 text-center">Cortesía</th>
              <th className="w-20">Costo fijo cortesía</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="">
            {dataMemo.length > 0 ? (
              dataMemo.map((row, index) => (
                <>
                  <tr key={index}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="d-flex justify-content-start w-100 flex-column fs-012 font-weight-mormal text-primary">
                          <input
                            className="form-control"
                            type="text"
                            value={row.nombre}
                            placeholder={'Nombre del producto'}
                            maxLength={800}
                            onChange={(event) => {
                              let nombreProducto = event.target.value;
                              if (nombreProducto) {
                                handleNameProduct(nombreProducto, row.id);
                              }
                            }}
                          />
                          {row.nombre && row.nombre.length > 750 && (
                            <small className="text-warning">
                              {row.nombre.length}/800 caracteres
                            </small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="d-flex justify-content-start flex-column fs-012 text-gray-600">
                          <NumericFormat
                            className="form-control w-150px"
                            thousandSeparator={true}
                            prefix="$"
                            decimalScale={2}
                            fixedDecimalScale={true}
                            allowNegative={false}
                            allowLeadingZeros={false}
                            isAllowed={(values: any) => {
                              const { floatValue } = values;
                              const integerPart = Math.floor(floatValue);
                              return integerPart.toString().length <= 9;
                            }}
                            value={row.precio_venta}
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              costChange(floatValue, row.id);
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="d-flex justify-content-start flex-column fs-012 text-gray-600">
                          <NumericFormat
                            className="form-control w-150px"
                            thousandSeparator={true}
                            prefix="$"
                            decimalScale={2}
                            fixedDecimalScale={true}
                            allowNegative={false}
                            allowLeadingZeros={false}
                            isAllowed={(values: any) => {
                              const { floatValue } = values;
                              const integerPart = Math.floor(floatValue);
                              return integerPart.toString().length <= 9;
                            }}
                            value={row.util_proveedor}
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              handleUtilProveedor(floatValue, row.id);
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="form-check form-check-custom form-check-solid d-flex justify-content-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!row.aplica_costo_fijo_cortesia}
                          onChange={(e) => handleCortesiaCheck(e.target.checked, row.id)}
                          title="Aplicar costo fijo para tickets con Cortesía"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="d-flex justify-content-start flex-column fs-012 text-gray-600">
                          <NumericFormat
                            className="form-control w-150px"
                            thousandSeparator={true}
                            prefix="$"
                            decimalScale={2}
                            fixedDecimalScale={true}
                            allowNegative={false}
                            allowLeadingZeros={false}
                            disabled={false}
                            placeholder={'Costo fijo'}
                            isAllowed={(values: any) => {
                              const { floatValue } = values;
                              const integerPart = Math.floor(floatValue);
                              return integerPart.toString().length <= 9;
                            }}
                            value={row.costo_fijo_cortesia}
                            onValueChange={(values: any) => {
                              const { floatValue } = values;
                              handleCostoCortesia(floatValue, row.id);
                            }}
                          />
                        </div>
                      </div>
                    </td>

                    {row.id ? (
                      <>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="d-flex justify-content-start flex-column fs-012 text-gray-600">
                              <div
                                className="badge badge-light-danger  text-hover-danger fw-bold px-4 py-3 cursor-pointer"
                                title="Eliminar producto"
                                onClick={() => {
                                  deleteRowById(row.id);
                                }}
                              >
                                <KTSVG
                                  path="/media/icons/duotune/general/gen027.svg"
                                  className="svg-icon-4"
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </>
                    ) : null}
                  </tr>
                </>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  Sin productos
                </td>
              </tr>
            )}

            <tr>
              <td colSpan={9}>
                <div className="d-flex align-items-center">
                  <div
                    className="btn btn-light-primary btn-sm mb-10"
                    onClick={AddRow}
                  >
                    <KTSVG
                      path="/media/icons/duotune/arrows/arr075.svg"
                      className="svg-icon-5"
                    />
                    Agregar Producto
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
};

export { ProductsList };
